<template>
    <Button
        class="campaign-edit__header-action-btn campaign-edit__header-action-btn--export"
        data-testid="editor-header__import-export-button"
        @click="importExportModalVisible = true"
    >
        Import/Export
    </Button>
    <ImportExportModal
        v-if="importExportModalVisible"
        :create-new-group-values="createNewGroupValues"
        :language-import-complete="languageImportComplete"
        :language-import-feedback="languageImportFeedback"
        @exportLanguage="onExportLanguage"
        @exportValuesTemplate="onExportValuesTemplate"
        @languagesUpdated="onSimplifiedLanguageUpdate"
        @close="importExportModalVisible = false"
    />
</template>

<script lang="ts">
import { defineComponent, nextTick } from "vue";
import type { EditableGroupValue } from "hoxton-types";

import { USER_ACTIONS } from "hoxton-shared/enums/user";

import ImportExportModal from "@/components/Campaign/ImportExportModal.vue";
import { userCan } from "@/services/Auth";
import addGroupValuesMutation from "@/apollo/mutations/v2/AddEditableGroupValues.gql";
import { CampaignAction } from "@/store/modules/campaign";
import ImportExport from "@/services/ImportExport";
import { updateLanguages } from "@/services/Language";
import { localeToFriendlyName, type LanguageCode } from "@/utils/languages";
import bus from "@/bus";

export default defineComponent({
    name: "ImportExportContent",
    components: { ImportExportModal },

    props: {
        sandboxMode: {
            type: Boolean,
            required: true
        }
    },

    data() {
        return {
            importExportModalVisible: false,
            importExportService: null as null | ImportExport,
            languageImportComplete: false,
            languageImportFeedback: { message: "", languages: [] } as {
                message: string;
                languages: LanguageCode[];
                newLanguages?: string;
            }
        };
    },

    computed: {
        campaignId() {
            return this.$route.params.campaignId as string;
        },

        clientId() {
            return this.$route.params.clientId as string;
        },

        canSeeRestrictedEditables() {
            return userCan(USER_ACTIONS.canManageRestrictedEditables, {
                campaignId: this.campaignId,
                clientId: this.clientId
            });
        },

        graphqlContext() {
            return {
                useLocalDevLink: this.sandboxMode
            };
        },

        campaignLanguages() {
            return this.$store.state.campaign.languages;
        }
    },

    created() {
        this.importExportService = new ImportExport(this.$apollo, this.$store);
    },

    methods: {
        async addEditableGroupValues(editableGroupName: string, values: string[]) {
            return this.$apollo.mutate({
                mutation: addGroupValuesMutation,
                variables: {
                    editableGroupName,
                    values,
                    campaignId: this.campaignId
                },
                context: this.graphqlContext
            });
        },

        async createNewGroupValues(groupName: string, newGroupValues: string[] = []) {
            const {
                data: { addEditableGroupValues: createdGroups }
            } = await this.addEditableGroupValues(groupName, newGroupValues);

            (createdGroups as EditableGroupValue[]).forEach(({ _id, value }) => {
                this.$store.dispatch(CampaignAction.AddEditableGroupValue, {
                    groupName,
                    editableGroupValue: { value, _id }
                });
            });

            return createdGroups;
        },

        onExportLanguage(language = "en") {
            this.importExportService?.exportXLSTranslationsTemplate(language, this.canSeeRestrictedEditables);
        },

        onExportValuesTemplate(type: "global" | "template") {
            this.importExportService?.exportXLSValuesTemplate(type, this.canSeeRestrictedEditables);
        },

        async onSimplifiedLanguageUpdate({
            languageData
        }: {
            languageData: { languages: LanguageCode[] } & Record<LanguageCode, Record<string, string>>;
        }) {
            this.languageImportComplete = false;
            // need to add this as the code changes too quickly and the modal couldn't set the correct ui state
            await nextTick();

            this.languageImportFeedback = { message: "", languages: languageData.languages };

            const phrasesCount = languageData.languages.reduce((acc, language) => {
                this.importExportService?.simplifiedLanguageUpdate({
                    language,
                    languageMapping: languageData[language],
                    canSeeRestrictedEditables: this.canSeeRestrictedEditables
                });
                return acc + Object.keys(languageData[language]).length;
            }, 0);

            const langCountText = (count: number, showCount = true) => {
                const form = count === 1 ? "language" : "languages";

                if (showCount) {
                    return `${count} ${form}`;
                }

                return form;
            };

            this.languageImportFeedback.message = `${phrasesCount} phrases in ${langCountText(
                languageData.languages.length
            )} have been applied in the editor.`;
            const languagesToAdd = languageData.languages.filter(lang => !this.campaignLanguages.includes(lang));
            if (languagesToAdd.length) {
                this.onLanguageUpdate(languagesToAdd.concat(this.campaignLanguages));
                const langsText = languagesToAdd.map(l => localeToFriendlyName(l));
                this.languageImportFeedback.newLanguages = `${languagesToAdd.length} new ${langCountText(
                    languagesToAdd.length,
                    false
                )} (${langsText}) have been added to the campaign`;
            }

            this.languageImportComplete = true;
        },

        async onLanguageUpdate(languages: LanguageCode[]) {
            try {
                this.$snackbar.info("Updating Languages...");

                await updateLanguages(this.campaignId, languages);

                this.$snackbar.success("Languages updated.");
            } catch (e) {
                this.$snackbar.error("We could not update languages", `\n${e as string}`, {
                    duration: 0,
                    closable: true
                });
            } finally {
                bus.$emit("percentLoadEvent", 100);
            }
        }
    }
});
</script>
